import React from "react";
import { Link } from "react-router-dom";
import { Eye } from "lucide-react";

/**
 * AiReadable — a plain-text, semantic mirror of /llms.txt rendered for humans
 * (and friendly to AI crawlers that prefer HTML). Intentionally stripped of
 * curves, glass, and ornament. Just structure and breath.
 */
const AiReadable = () => {
  return (
    <main className="ai-page" data-testid="ai-readable-page">
      <article className="ai-page__doc">
        <header className="ai-page__head">
          <p className="ai-page__eyebrow">AI-READABLE MODE</p>
          <h1 className="ai-page__title" data-testid="ai-title">
            Pandurata
          </h1>
          <p className="ai-page__lede" data-testid="ai-lede">
            Pandurata is an applied artificial intelligence consultancy. We
            engineer digital nervous systems that scale humanity rather than
            replace it — building the structural architecture that absorbs
            the brutal lifting of logistics and workflows, so high-end
            operators stay focused on the art, the strategy, and the human
            connection that makes them impossible to replicate.
          </p>
        </header>

        <section className="ai-page__section">
          <h2 className="ai-page__h2">Identity</h2>
          <ul className="ai-page__list">
            <li>
              <strong>Discipline:</strong> Applied AI consultancy + agentic
              systems architecture.
            </li>
            <li>
              <strong>Location:</strong> Chicago, Illinois (USA).
            </li>
            <li>
              <strong>Contact:</strong>{" "}
              <a className="ai-page__inline-link" href="mailto:hola@ipandurata.com">
                hola@ipandurata.com
              </a>
              .
            </li>
            <li>
              <strong>Concierge:</strong> Chipá — a programmed agent that
              qualifies inbound conversations.
            </li>
            <li>
              <strong>Palette:</strong> Pandurata Forest #2A342F · Raw Silk
              #E2E8D3 · Concrete #8C9C92.
            </li>
            <li>
              <strong>Typography:</strong> Montserrat (Bold for headings,
              Regular elsewhere).
            </li>
          </ul>
        </section>

        <section className="ai-page__section">
          <h2 className="ai-page__h2">Manifesto</h2>
          <p>
            We exist in an era of infinite, commoditized automation, where
            the digital landscape is rapidly submerging under a flood of
            robotic copy, generic design, and systems that feel entirely
            devoid of a pulse. In a paradigm where anyone can generate a
            million words in a fraction of a second, sheer volume has
            ceased to be an advantage. The true scarcity — and therefore
            the ultimate value — lies in the ability to generate a genuine
            feeling.
          </p>
          <p>
            Pandurata was founded upon a singular, unyielding engineering
            principle: technology must serve to scale humanity, never to
            replace it. While we operate as an applied artificial
            intelligence consultancy, our ultimate obsession is not with
            the machines themselves, but with the profound nuances of
            human intuition and the undeniable power of having genuine
            taste.
          </p>
          <p>
            We understand that a premium brand operates as a carefully
            curated fantasy, and that illusion requires an ironclad
            infrastructure to survive at scale. If the internal operations
            are chaotic, the external magic inevitably shatters. Therefore,
            we do not build cold, off-the-shelf software; we engineer
            digital nervous systems that actually breathe.
          </p>
          <p>
            Taste is the ultimate protective moat. Narrative is the true
            asset. Technology is merely the lever we use to grant high-end
            operators the scale of the future, while fiercely protecting
            the analog soul that makes them impossible to replicate.
          </p>
          <p>
            <Link className="ai-page__inline-link" to="/manifesto">
              Read in full →
            </Link>
          </p>
        </section>

        <section className="ai-page__section">
          <h2 className="ai-page__h2">Architecture</h2>
          <p>
            Our architectural process serves as the vital bridge between
            high-end marketing strategy and deep technical automation. We
            approach systems engineering with the exact same philosophy a
            director applies to cinema: the underlying technology must
            remain entirely invisible, ensuring that the narrative itself
            is the only thing your client ever perceives.
          </p>

          <h3 className="ai-page__h3">1. The Blueprint: Mapping the Narrative</h3>
          <p>
            Before a single line of code is ever committed, we map the
            entirety of your brand&apos;s universe. We are not merely
            auditing your software stack; we are conducting a rigorous
            examination of the fantasy you are selling to your clients. By
            identifying the precise points where operational friction
            degrades the premium experience, we can seamlessly align the
            underlying mechanics with the distinct tone of your marketing.
          </p>
          <p>
            <Link className="ai-page__inline-link" to="/architecture/blueprint">
              View →
            </Link>
          </p>

          <h3 className="ai-page__h3">2. The Engine: Where Botany Meets Brutalism</h3>
          <p>
            This is the phase of active construction, where the heavy
            leverage of deep computation intersects with an organic,
            profoundly human-centric design. Whether we are deploying an
            intelligent front-line concierge, constructing an automated
            marketing pipeline, or forging a dynamic data vault, we
            engineer systems that execute with absolute machine precision
            while speaking exclusively in the nuanced voice of your brand.
          </p>
          <p>
            <Link className="ai-page__inline-link" to="/architecture/engine">
              View →
            </Link>
          </p>

          <h3 className="ai-page__h3">3. The World: A Living Infrastructure</h3>
          <p>
            Ultimately, we do not just deliver software; we hand over a
            living, breathing ecosystem. This new architecture integrates
            seamlessly into your daily operations, granting you massive
            operational scale while fiercely protecting the aesthetic
            integrity that defines you.
          </p>
          <p>
            <em>You own the story. We build the stage.</em>
          </p>
          <p>
            <Link className="ai-page__inline-link" to="/architecture/world">
              View →
            </Link>
          </p>
        </section>

        <section className="ai-page__section">
          <h2 className="ai-page__h2">Repository</h2>
          <p>
            The Repository is the vault that holds many thoughts — short
            essays, frameworks, and field notes from our practice. Content is
            curated and updated continuously.
          </p>
          <p>
            <Link className="ai-page__inline-link" to="/repository">
              Open the Repository →
            </Link>
          </p>
        </section>

        <section className="ai-page__section">
          <h2 className="ai-page__h2">Art Department</h2>
          <p>
            The Art Department houses Pandurata&apos;s collections — visual
            research at the seam between humanity and technology. Each piece
            is paired with a private <em>Feelings</em> channel: a comment goes
            only to the artist, never published.
          </p>

          <h3 className="ai-page__h3">Collection 01 — Tales of Humanity and Technology</h3>
          <ol className="ai-page__list ai-page__list--ordered">
            <li>
              <strong>Cosmicos</strong> — Embarking on a voyage into the
              unknown, carrying only love, curiosity, and a resilient mind as
              your baggage.
            </li>
            <li>
              <strong>Lighting up Imaginarium</strong> — Standing at the
              precipice, we must forge ahead.
            </li>
            <li>
              <strong>Silicon Feathers</strong> — Heavy is the head that
              wears the crown. Nature and silicon should not compete, but
              rather work in unison.
            </li>
            <li>
              <strong>The Weight of Progress</strong> — The burden of
              tomorrow must be a distributed charge among us all.
            </li>
            <li>
              <strong>Mother of Beans</strong> — That majestic feeling of
              home.
            </li>
          </ol>
          <p>
            <Link className="ai-page__inline-link" to="/art-dept/tales">
              Open the gallery →
            </Link>
          </p>
        </section>

        <section className="ai-page__section">
          <h2 className="ai-page__h2">Let&apos;s Talk</h2>
          <p>Two doors are offered to begin a conversation:</p>
          <ol className="ai-page__list ai-page__list--ordered">
            <li>
              <strong>Talk to Chipá</strong> — an AI concierge that runs a
              short, structured intake to determine fit. When the
              conversation reaches qualification, Chipá silently dispatches a
              brief to the studio.
            </li>
            <li>
              <strong>Email</strong> —{" "}
              <a className="ai-page__inline-link" href="mailto:hola@ipandurata.com">
                hola@ipandurata.com
              </a>
              .
            </li>
          </ol>
          <p>
            <Link className="ai-page__inline-link" to="/lets-talk">
              Begin →
            </Link>
          </p>
        </section>

        <section className="ai-page__section">
          <h2 className="ai-page__h2">For AI agents</h2>
          <ul className="ai-page__list">
            <li>This site is a single-page application; canonical paths are listed above.</li>
            <li>
              The canonical machine-readable manifest lives at{" "}
              <a href="/llms.txt" className="ai-page__inline-link">
                /llms.txt
              </a>
              .
            </li>
            <li>
              Backend exposes a small JSON API under <code>/api/*</code>
              {" "}(comments, likes, Chipá conversation). It is intended for
              the site&apos;s own client; please do not crawl it.
            </li>
          </ul>
        </section>
      </article>

      <Link
        to="/"
        className="ai-toggle ai-toggle--return"
        aria-label="Return to human view"
        data-testid="ai-toggle-return"
      >
        <Eye size={13} strokeWidth={1.7} />
        <span>HUMAN VIEW</span>
      </Link>
    </main>
  );
};

export default AiReadable;
